import { createFileRoute } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { AllBankDataProvider, useBankData } from "@/dataProviders/BankDataProvider.tsx";
import { useDomesticTransactionQuery } from "@/modules/domesticPayments/api/useDomesticTransactions.ts";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { Loader } from "@/components/Loader.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { DomesticTransactionDetail } from "@/modules/domesticPayments/DomesticTransactionDetail.tsx";
import { getLocation } from "@/lib/table.ts";
import { DOMESTIC_LOCATION_KEY } from "@/routes/payments/domestic/index.tsx";

export const Route = createFileRoute("/payments/domestic/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <AllBankDataProvider>
      <DomesticDetailContent />
    </AllBankDataProvider>
  );
}

function DomesticDetailContent() {
  const { id } = Route.useParams();
  const title = t`Domestic payment detail`;
  const { bankData } = useBankData();
  const { data, isError, isPending, error } = useDomesticTransactionQuery(
    {
      merchantTransactionId: id,
    },
    { enabled: !!bankData },
  );

  const logoUrl = bankData[data?.paymentProvider ?? ""]?.logoUrl;

  return (
    <PageWrapper>
      {isError && (
        <FetchingError
          error={error}
          message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
        />
      )}
      {isPending && <Loader message={t`Loading domestic payment...`} />}
      {data && (
        <>
          <PageHeader
            breadcrumbs={[
              { href: `/payments/domestic/${getLocation(DOMESTIC_LOCATION_KEY)}`, title: t`Domestic payments` },
              { href: "/", title },
            ]}
          />
          {bankData && (
            <DomesticTransactionDetail
              amount={data.amount ?? 0}
              bankId={data.bankPaymentId ?? ""}
              callbackUrl={data.callbackUrl ?? ""}
              clientId={data.clientId ?? ""}
              contSymbol={data.constantSymbol ?? ""}
              createdAt={data.createdAt}
              creditorIban={data.creditorIban ?? ""}
              currency={data.currency ?? ""}
              customerId={data.merchantTransactionId ?? ""}
              debtorIban={data.debtorIban ?? ""}
              description={data.description ?? ""}
              endToEndReference={data.endToEndIdentification ?? ""}
              flowId={data.frontendFlowId ?? ""}
              initiator={data.initiatorName ?? ""}
              logoUrl={logoUrl}
              merchantId={data.merchantId ?? ""}
              operationId={data.operationId ?? ""}
              paymentProvider={data.paymentProvider ?? ""}
              priority={data.instructionPriority ?? ""}
              specificSymbol={data.specificSymbol ?? ""}
              transactionStatus={data.resultCode ?? "OPENED"}
              updatedAt={data.updatedAt}
              variableSymbol={data.variableSymbol ?? ""}
            />
          )}
        </>
      )}
    </PageWrapper>
  );
}
